// @flow

import React from 'react';
import { withTranslation } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardMedia from '@material-ui/core/CardMedia';
import CardContent from '@material-ui/core/CardContent';

import ApplyOptions from '../apply-options';
import { getCustomerLogoPath } from '../../util/customer';
import { IMAGE_PATH } from '../../util/config';
import JobCompensation from '../job-compensation';
import { InactiveJob } from '../inactive-job';
import { CareersPageButton } from '../careers-page-button';

const styles = theme => ({
  media: {
    height: '200px',
  },
  darkOverlay: {
    height: '200px',
    position: 'relative',
    marginBottom: '-200px',
    bottom: '200px',
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
  },
  card: {
    width: '100%',
  },
  overlay: {
    position: 'relative',
    left: '15px',
    bottom: '75px',
    marginBottom: '-25px',
    fontFamily: 'Roboto',
    zIndex: 100,
  },
  careersContainer: {
    textAlign: 'center',
    margin: '10px 0',
  },
  logo: {
    display: 'block',
    position: 'absolute',
    right: '30px',
    top: '-110px',
    maxWidth: '120px',
    marginBottom: '10px',
    background: 'rgba(255, 255, 255, 0.85)',
    padding: '5px',
  },
  header: {
    paddingBottom: '20px',
  },
  headline: {
    lineHeight: '1.1em',
    marginBottom: 0,
    align: 'left',
    color: 'white',
  },
  caption: {
    color: 'white',
  },
  applyText: {
    margin: '10px',
    padding: '0px 15px 0px 15px',
    fontWeight: 300,
  },
  applyTextBold: {
    margin: '8px 0px 8px 0px',
    padding: '0px 15px 0px 15px',
    fontSize: 'large',
    fontWeight: 400,
  },
  applyBody: {
    padding: '10px',
  },
});

type Props = {
  classes: Object,
  job: Job,
  jobCompensation: Object | null,
  org: Org,
  t: (value: string, opts?: any) => string,
}

type State = {
}

class JobDescriptionSEOComponent extends React.PureComponent<Props, State> {
  render() {
    const logoPath = getCustomerLogoPath(this.props.org);
    const { classes, job, jobCompensation, org, t } = this.props;
    const {
      relevantExperience,
      relevantLicenses,
      status
    } = job;
    let requirements = [];
    if (relevantExperience) {
      requirements = requirements.concat(relevantExperience.map(exp => `${exp.experienceName}${exp.isRequired ? '' : ` (${t('optional')})`}`));
    }
    if (relevantLicenses) {
      requirements = requirements.concat(relevantLicenses.map(lic => `${lic.license.license_name}${lic.isRequired ? '' : ` (${t('optional')})`}`));
    }

    const isJobHiring = ['PUBLISHED', 'ACTIVE'].includes(status);

    return (
      <div style={{ width: '100%' }}>
        <Card className={classes.card}>
          { job.imagePath ?
            <CardMedia
              image={`${IMAGE_PATH}/${job.imagePath}`}
              className={classes.media}
            /> :
            <CardContent
              className={classes.media}
            />
          }
          <div className={classes.darkOverlay} />
          <div className={classes.overlay}>
            <a href={org.website} target="blank" rel="noopener noreferrer">
              <img src={logoPath} className={classes.logo} alt="Home" />
            </a>
            <div className={classes.jobHeader}>
              <Typography variant="h5" className={classes.headline} gutterBottom>{job.title}{job.org ? ` at ${job.org.name}` : ''}</Typography>
              {job.city && job.state &&
                <Typography variant="caption" className={classes.caption}>{job.city}, {job.state}</Typography>
                }
              <br />
              <JobCompensation
                  offeredCompensation={jobCompensation}
                  typographyClass={classes.caption}
                  typographyVariant="caption"
                  variant="typography"
                />
            </div>
          </div>
          {isJobHiring ? (
            <>
              <ApplyOptions />
              { this.props.job.shortDescription ?
                <Typography variant="subtitle1" className={classes.applyText}>
                  {/* eslint-disable-next-line */}
                  <div dangerouslySetInnerHTML={{ __html: this.props.job.shortDescription }} />
                </Typography>
                : null
              }
              <Grid container alignItems="center" justify="center" className={classes.applyBody}>
                <Grid item xs={12}>
                  <Typography variant="h5">
                    {t('description')}
                  </Typography>
                  <Typography variant="subtitle1" className={classes.applyText}>
                    {/* eslint-disable-next-line */}
                    <div dangerouslySetInnerHTML={{ __html: this.props.job.description }} />
                  </Typography>
                </Grid>
                <JobCompensation
                  offeredCompensation={jobCompensation}
                  typographyClass={classes.applyTextBold}
                  variant="grid"
                  typographyVariant="subtitle1"
                  gridText={t('compensation.offeredCompensation')}
                  gridClass={classes.applyBody}
                />
                { requirements.length > 0 &&
                  <Grid item xs={12}>
                    <Typography variant="h5">
                      {t('experienceAndLicenseRequirements')}
                    </Typography>
                    <Typography variant="subtitle1" className={classes.applyText}>
                      <ul>
                        { requirements.map(requirement => <li key={requirement}>{requirement}</li>) }
                      </ul>
                    </Typography>
                  </Grid>
                }
              </Grid>
            </>
          ) : (
            <InactiveJob job={job} org={org} />
          )}
            <CareersPageButton org={org}/>
        </Card>
      </div>
    );
  }
}

export const JobDescriptionSEO = withTranslation()(withStyles(styles)(JobDescriptionSEOComponent));
